import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { BasketProvider } from './hooks/useBasket';
import Login from './pages/Login';
import Catalog from './pages/OrderGuide';
import OrderHistory from './pages/OrderHistory';
import Invoices from './pages/Invoices';
import OrderGuide from './pages/NewOrderGuide';
import Basket from './pages/Basket';
import Specials from './pages/Specials';
import ProductRequest from './pages/ProductRequest';
import Admin from './pages/Admin';
import Purchase from './pages/Purchase';
import Sales from './pages/Sales';
import ReceiptCapture from './pages/ReceiptCapture';
import SetupPassword from './pages/SetupPassword';
import Team from './pages/Team';
import Approvals from './pages/Approvals';
import Addresses from './pages/Addresses';
import Feedback from './pages/Feedback';
import ServiceRequest from './pages/ServiceRequest';
import NewCustomer from './pages/NewCustomer';
import StaffHome from './pages/StaffHome';
import ConnectClaude from './pages/ConnectClaude';
import StaffPreview from './pages/StaffPreview';
import Service from './pages/Service';
import ServiceFeedback from './pages/ServiceFeedback';
import Reports from './pages/Reports';
import Fulfillment from './pages/Fulfillment';
import Layout from './components/Layout';
import { parseHash } from './lib/sites';

function AppRoutes() {
  const { user, loading, login } = useAuth();
  // Hostname- and viewport-aware default page:
  //   manage.silverservice.com → admin portal
  //   any host on a phone (≤768px) → order-guide (customer's curated
  //     re-order list, much shorter than the full catalog and what most
  //     B2B customers want first when they open the app)
  //   any host on desktop → catalog (browse-friendly default)
  //
  // Both domains serve the same SPA bundle; this is just where they land
  // when there's no explicit hash route in the URL.
  const isManageHost = typeof window !== 'undefined'
    && /^manage\./i.test(window.location.hostname || '');
  const isPurchaseHost = typeof window !== 'undefined'
    && /^purchase\./i.test(window.location.hostname || '');
  const isReceiptsHost = typeof window !== 'undefined'
    && /^receipts\./i.test(window.location.hostname || '');
  const isSalesHost = typeof window !== 'undefined'
    && /^sales\./i.test(window.location.hostname || '');
  const isNewCustomerHost = typeof window !== 'undefined'
    && /^newcustomer\./i.test(window.location.hostname || '');
  const isStaffHost = typeof window !== 'undefined'
    && /^staff\./i.test(window.location.hostname || '');
  const isServiceHost = typeof window !== 'undefined'
    && /^service\./i.test(window.location.hostname || '');
  const isReportsHost = typeof window !== 'undefined'
    && /^reports\./i.test(window.location.hostname || '');
  const isFulfillmentHost = typeof window !== 'undefined'
    && /^fulfillment\./i.test(window.location.hostname || '');
  const isMobileViewport = typeof window !== 'undefined'
    && window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
  const defaultPage = isStaffHost ? 'staff'
    : isServiceHost ? 'service'
    : isReportsHost ? 'reports'
    : isFulfillmentHost ? 'fulfillment'
    : isReceiptsHost ? 'receipt-capture'
    : isPurchaseHost ? 'purchase'
    : isSalesHost ? 'sales'
    : isNewCustomerHost ? 'new-customer'
    : isManageHost ? 'admin'
    : (isMobileViewport ? 'order-guide' : 'catalog');
  // An explicit ?page= wins over the hostname default — used when something
  // outside the app (the QuickBooks OAuth callback) needs to drop a staff
  // member into a specific screen.
  const requestedPage = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('page');
  const [page, setPage] = useState(requestedPage || defaultPage);

  useEffect(() => {
    // Handle path-based /setup-password URLs (from invite emails)
    if (window.location.pathname === '/setup-password') {
      setPage('setup-password');
      return;
    }
    // Only the page part: staff sign-in passes parameters after it
    // ("#sales?handoff=…", lib/staffSession.js).
    setPage(parseHash().page || defaultPage);
    const onHash = () => setPage(parseHash().page || defaultPage);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [defaultPage]);

  // Setup-password page — always accessible (invite link flow)
  if (page === 'setup-password') {
    return <SetupPassword onLogin={() => {
      window.location.href = '/';
    }} />;
  }

  // New-customer intake — a fully public account-application form. No auth,
  // rendered before the loading/login gates. Reachable at
  // newcustomer.silverservice.com or via the #new-customer hash.
  if (page === 'new-customer') {
    return <NewCustomer />;
  }

  // A customer's feedback on a service visit, from the link the office emails
  // them (orders.silverservice.com/#service-feedback?t=…). Public, like the
  // intake form: the signed link is the credential.
  if (page === 'service-feedback') {
    return <ServiceFeedback />;
  }

  // Staff start page — directions to every site, and where staff sign in
  // (staff.silverservice.com or #staff). See docs/site-map.md.
  if (page === 'staff') {
    return <StaffHome />;
  }

  // Connect Claude — where a staff member lets their Claude read Silver
  // Service as them (docs/claude-connector.md). Claude's sign-in sends the
  // browser to staff.silverservice.com/#connect-claude?request=….
  if (page === 'connect-claude') {
    return <ConnectClaude />;
  }

  // Staff looking at the ordering site as a customer of their choosing,
  // look-only. orders.silverservice.com/#staff-preview, opened from the start
  // page; a staff login is not a customer login.
  if (page === 'staff-preview') {
    return <StaffPreview />;
  }

  // Service desk — technicians' jobs, equipment and customer service requests.
  // service.silverservice.com or #service.
  if (page === 'service') {
    return <Service />;
  }

  // Fulfillment — the office and drivers: what goes out, delivery records,
  // problem orders. fulfillment.silverservice.com or #fulfillment.
  if (page === 'fulfillment') {
    return <Fulfillment />;
  }

  // Reports — owners-only company reporting. reports.silverservice.com or #reports.
  if (page === 'reports') {
    return <Reports />;
  }

  // Purchasing tool — standalone, behind the staff sign-in.
  // Reachable at purchase.silverservice.com or via the #purchase hash.
  if (page === 'purchase') {
    return <Purchase />;
  }

  // Sales tool — standalone, behind the staff sign-in (sales_rep / owner).
  // Reachable at sales.silverservice.com or via the #sales hash.
  if (page === 'sales') {
    return <Sales />;
  }

  // Receipt capture — standalone mobile surface for field staff, behind the
  // staff sign-in. Reachable at receipts.silverservice.com or via #receipt-capture.
  if (page === 'receipt-capture') {
    return <ReceiptCapture />;
  }

  // Admin page — always routable; behind the staff sign-in
  if (page === 'admin') {
    return <Admin />;
  }

  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#0a0a0a'}}>
      <div style={{color:'#C8A96E',fontFamily:'serif',fontSize:'1.5rem'}}>Silver Service</div>
    </div>
  );

  if (!user) return <Login />;

  return (
    <Layout page={page} setPage={setPage}>
      {page === 'catalog'       && <Catalog />}
      {page === 'order-guide'   && <OrderGuide />}
      {page === 'specials'      && <Specials />}
      {page === 'order-history' && <OrderHistory />}
      {page === 'invoices'      && user?.features?.invoices && <Invoices />}
      {page === 'basket'        && <Basket />}
      {page === 'request'       && <ProductRequest />}
      {page === 'team'             && <Team />}
      {page === 'approvals'        && <Approvals />}
      {page === 'addresses'        && <Addresses />}
      {page === 'service-request'  && <ServiceRequest />}
      {page === 'feedback'         && <Feedback />}
    </Layout>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BasketProvider>
        <AppRoutes />
      </BasketProvider>
    </AuthProvider>
  );
}
